import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface RowAction {
  label: string;
  onClick: () => void;
  destructive?: boolean;
  separator?: boolean;
}

/**
 * Render an ellipsis (…) button that opens a small dropdown of actions.
 *
 * Implementation notes:
 *   - The dropdown uses `position: fixed` (not absolute) and is positioned
 *     against the viewport. That way it can never be clipped by an ancestor
 *     with `overflow: hidden` / `overflow: auto` — which is exactly what was
 *     happening inside tables, where rows clip the bottom of the menu.
 *   - On open, we measure the trigger rect and decide whether to open
 *     downward (default) or upward (if there isn't enough room below).
 *   - We recompute on scroll/resize so the dropdown stays anchored if the
 *     page moves while it's open.
 */
export function RowActionsMenu({ actions }: { actions: RowAction[] }) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number; placement: 'down' | 'up' }>({ top: 0, left: 0, placement: 'down' });

  // (Re)position whenever the menu opens or the viewport scrolls/resizes.
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const reposition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const menuH = menuRef.current?.offsetHeight ?? 200; // rough estimate before first paint
      const menuW = menuRef.current?.offsetWidth ?? 180;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      // Open upward if there isn't enough room below AND there's more space above.
      const placement: 'down' | 'up' = (spaceBelow < menuH + 16 && spaceAbove > spaceBelow) ? 'up' : 'down';
      const top = placement === 'down'
        ? Math.min(rect.bottom + 4, window.innerHeight - menuH - 8)
        : Math.max(rect.top - menuH - 4, 8);
      // Right-align with the trigger, clamped to the viewport.
      const left = Math.max(8, Math.min(rect.right - menuW, window.innerWidth - menuW - 8));
      setPos({ top, left, placement });
    };
    reposition();
    // Re-measure after the menu mounts so its real height drives placement.
    const raf = requestAnimationFrame(reposition);
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [open]);

  // Click-outside + Escape to close.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (menuRef.current?.contains(t)) return;
      setOpen(false);
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <span style={{ display: 'inline-block' }} onClick={(e) => e.stopPropagation()}>
      <button
        ref={triggerRef}
        className="btn btn--ghost"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        style={{ padding: '0 8px', fontSize: 18, lineHeight: 1 }}
      >
        ⋯
      </button>
      {open && (
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            minWidth: 180,
            background: 'var(--surface-raised)',
            border: '1px solid var(--border-strong)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)',
            zIndex: 2500,
            padding: 4,
          }}
        >
          {actions.map((a, i) => a.separator
            ? <hr key={`sep-${i}`} style={{ margin: '4px 0', border: 'none', borderTop: '1px solid var(--border-subtle)' }} />
            : (
              <button
                key={a.label}
                role="menuitem"
                onClick={() => { a.onClick(); setOpen(false); }}
                style={{
                  display: 'block',
                  width: '100%',
                  textAlign: 'left',
                  padding: '6px 10px',
                  background: 'transparent',
                  border: 'none',
                  fontSize: 13,
                  cursor: 'pointer',
                  color: a.destructive ? 'var(--danger-primary)' : 'var(--text-primary)',
                  borderRadius: 3,
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-sunken)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                {a.label}
              </button>
            ),
          )}
        </div>
      )}
    </span>
  );
}
